import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Switch,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
  Box,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  CircularProgress
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { useAuth } from '../../../AuthContext';
import { fetchData } from '../../../apiService';
import './AccountHeadModal.css';

const OtherOptionModal = ({
  open,
  onClose,
  modalAction,
  selectedItem,
  formData,
  setFormData,
  onSave,
  balanceSheetHeads = [],
  loading
}) => {
  const { companyId, token } = useAuth();
  const [errors, setErrors] = useState({});

  // Branch-wise opening balance states
  const [branches, setBranches] = useState([]);
  const [branchesLoading, setBranchesLoading] = useState(false);
  const [apiOpeningBalances, setApiOpeningBalances] = useState(null);

  const today = new Date().toISOString().split('T')[0];

  // Fetch branches when modal opens
  useEffect(() => {
    if (open && companyId && token) {
      fetchBranches();
    } else if (!open) {
      setBranches([]);
      setApiOpeningBalances(null);
    }
  }, [open, companyId, token]);

  useEffect(() => {
    if (selectedItem && modalAction !== 'create') {
      setApiOpeningBalances(selectedItem.OpeningBalances || []);
      setFormData({ ...selectedItem, OpeningBalances: [] });
    } else if (modalAction === 'create') {
      setApiOpeningBalances(null);
      setFormData(getDefaultFormData());
    }
  }, [selectedItem, modalAction]);

  const fetchBranches = async () => {
    setBranchesLoading(true);
    try {
      const response = await fetchData('BranchApi', `/${companyId}`, token);
      const data = response.response_data || response || [];
      setBranches(Array.isArray(data) ? data : [data]);
    } catch (error) {
      console.error('Error fetching branches:', error);
      setBranches([]);
    }
    setBranchesLoading(false);
  };

  // Merge branches with API opening balances once both are ready
  useEffect(() => {
    if (branches.length === 0) return;

    if (modalAction === 'create') {
      setFormData(prev => ({
        ...prev,
        OpeningBalances: branches.map(b => ({
          BranchId: b.Id,
          BranchName: b.Name || b.BranchName || '',
          OpeningBalance: 0,
          OpeningDate: today,
          IsPayableOrReceivable: false
        }))
      }));
    } else if ((modalAction === 'edit' || modalAction === 'view') && apiOpeningBalances !== null) {
      const merged = branches.map(b => {
        const found = apiOpeningBalances.find(r => r.BranchId === b.Id);
        return found
          ? { ...found, Id: found.Id || 0, BranchName: b.Name || b.BranchName || found.BranchName || '' }
          : {
              Id: 0,
              BranchId: b.Id,
              BranchName: b.Name || b.BranchName || '',
              OpeningBalance: 0,
              OpeningDate: today,
              IsPayableOrReceivable: false
            };
      });
      setFormData(prev => ({ ...prev, OpeningBalances: merged }));
    }
  }, [branches, modalAction, apiOpeningBalances]);

  const handleOpeningBalanceChange = (branchId, field, value) => {
    setFormData(prev => ({
      ...prev,
      OpeningBalances: (prev.OpeningBalances || []).map(row =>
        row.BranchId === branchId ? { ...row, [field]: value } : row
      )
    }));
  };

  const getDefaultFormData = () => {
    return {
      type: 'otherOption',
      Name: '',
      Description: '',
      SearchableName: '',
      BalanceSheetHeadId: null,
      IsPayableOrReceivable: false,
      IsActive: true,
      OpeningBalances: []
    };
  };

  const validateForm = () => {
    const newErrors = {};
    const getValue = (lowercase, pascalCase) => formData[lowercase] ?? formData[pascalCase];

    if (!(getValue('name', 'Name')?.toString().trim())) {
      newErrors.name = 'Name is required';
    }

    if (!(getValue('balanceSheetHeadId', 'BalanceSheetHeadId')?.toString().trim())) {
      newErrors.balanceSheetHeadId = 'Balance sheet head is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    if (field === 'Name') {
      setFormData(prev => ({
        ...prev,
        [field]: value,
        SearchableName: value?.toLowerCase().replace(/[^a-z0-9]/g, '') || ''
      }));
    }

    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleSave = () => {
    if (validateForm()) onSave();
  };

  const getModalTitle = () => {
    const actionNames = { create: 'New', edit: 'Edit', view: 'View' };
    const actionName = actionNames[modalAction] || 'New';
    return {
      title: `${actionName} Other Option`,
      subtitle: `${modalAction === 'create' ? 'Add a new' : modalAction === 'edit' ? 'Update' : 'View'} other option ${modalAction === 'create' ? 'to your accounts' : 'details'}`
    };
  };

  const renderForm = () => (
    <div className="form-container">
      {/* Basic Information Section */}
      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1e3a8a', mb: 0.5, mt: 0.5, fontSize: '0.75rem' }}>
        Basic Information
      </Typography>

      <div className="form-row">
        <div className="form-field required-field">
          <TextField
            fullWidth
            label="Name"
            placeholder="Enter name"
            value={formData.Name || formData.name || ''}
            onChange={(e) => handleInputChange('Name', e.target.value)}
            error={!!errors.name}
            helperText={errors.name}
            disabled={modalAction === 'view'}
            required
          />
        </div>
        <div className="form-field">
          <TextField
            fullWidth
            label="Searchable Name"
            placeholder="Auto-generated from name"
            value={formData.SearchableName || formData.searchableName || ''}
            onChange={(e) => handleInputChange('SearchableName', e.target.value)}
            disabled={modalAction === 'view'}
            helperText="Used for quick search (auto-generated)"
          />
        </div>
      </div>

      {/* Classification Section */}
      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1e3a8a', mb: 0.5, mt: 1, fontSize: '0.75rem' }}>
        Classification
      </Typography>

      <div className="form-row">
        <div className="form-field">
          <FormControl fullWidth error={!!errors.balanceSheetHeadId}>
            <InputLabel>Balance Sheet Head *</InputLabel>
            <Select
              value={formData.BalanceSheetHeadId || formData.balanceSheetHeadId || ''}
              onChange={(e) => handleInputChange('BalanceSheetHeadId', e.target.value)}
              disabled={modalAction === 'view'}
              label="Balance Sheet Head *"
            >
              <MenuItem value="">Select Balance Sheet Head</MenuItem>
              {balanceSheetHeads.map(head => (
                <MenuItem key={head.Id || head.id} value={head.Id || head.id}>
                  {head.Name || head.name}
                </MenuItem>
              ))}
            </Select>
            {errors.balanceSheetHeadId && <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>{errors.balanceSheetHeadId}</Typography>}
          </FormControl>
        </div>
        <div className="form-field">
          <FormControlLabel
            control={
              <Switch
                checked={formData.IsPayableOrReceivable ?? formData.isPayableOrReceivable ?? false}
                onChange={(e) => handleInputChange('IsPayableOrReceivable', e.target.checked)}
                disabled={modalAction === 'view'}
              />
            }
            label="Payable/Receivable"
          />
        </div>
      </div>

      {/* Branch-wise Opening Balances */}
      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1e3a8a', mb: 0.5, mt: 1, fontSize: '0.75rem' }}>
        Opening Balances (Branch-wise)
      </Typography>
      <Box sx={{ mb: 0.75 }}>
        {branchesLoading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
            <CircularProgress size={14} />
            <span style={{ fontSize: '0.72rem', color: '#64748b' }}>Loading branches...</span>
          </Box>
        ) : (
          <Box sx={{ border: '1px solid #e5e7eb', borderRadius: '6px', overflow: 'hidden' }}>
            <Table size="small" sx={{ tableLayout: 'fixed' }}>
              <TableHead>
                <TableRow sx={{ background: '#f1f5f9' }}>
                  <TableCell sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#1e3a8a', py: 0.5, px: 1, width: '28%' }}>Branch</TableCell>
                  <TableCell sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#1e3a8a', py: 0.5, px: 1, width: '28%' }}>Opening Balance (₹)</TableCell>
                  <TableCell sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#1e3a8a', py: 0.5, px: 1, width: '26%' }}>Opening Date</TableCell>
                  <TableCell sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#1e3a8a', py: 0.5, px: 1, width: '18%', textAlign: 'center' }}>Payable</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(formData.OpeningBalances || []).map((row, idx) => (
                  <TableRow key={row.BranchId} sx={{ background: idx % 2 === 0 ? '#fff' : '#f8f9fa' }}>
                    <TableCell sx={{ fontSize: '0.72rem', py: 0.4, px: 1, color: '#374151', fontWeight: 500 }}>
                      {row.BranchName}
                    </TableCell>
                    <TableCell sx={{ py: 0.4, px: 1 }}>
                      <TextField
                        size="small"
                        type="number"
                        value={row.OpeningBalance ?? 0}
                        onChange={(e) => handleOpeningBalanceChange(row.BranchId, 'OpeningBalance', parseFloat(e.target.value) || 0)}
                        disabled={modalAction === 'view'}
                        inputProps={{ min: 0, style: { padding: '3px 6px', fontSize: '0.72rem' } }}
                        sx={{ '& .MuiOutlinedInput-root': { minHeight: '26px' }, width: '100%' }}
                      />
                    </TableCell>
                    <TableCell sx={{ py: 0.4, px: 1 }}>
                      <TextField
                        size="small"
                        type="date"
                        value={row.OpeningDate?.split('T')[0] || ''}
                        onChange={(e) => handleOpeningBalanceChange(row.BranchId, 'OpeningDate', e.target.value)}
                        disabled={modalAction === 'view'}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ style: { padding: '3px 6px', fontSize: '0.72rem' } }}
                        sx={{ '& .MuiOutlinedInput-root': { minHeight: '26px' }, width: '100%' }}
                      />
                    </TableCell>
                    <TableCell sx={{ py: 0.4, px: 1, textAlign: 'center' }}>
                      <Switch
                        size="small"
                        checked={row.IsPayableOrReceivable ?? false}
                        onChange={(e) => handleOpeningBalanceChange(row.BranchId, 'IsPayableOrReceivable', e.target.checked)}
                        disabled={modalAction === 'view'}
                      />
                    </TableCell>
                  </TableRow>
                ))}
                {(formData.OpeningBalances || []).length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} sx={{ fontSize: '0.72rem', color: '#9ca3af', textAlign: 'center', py: 1 }}>
                      No branches available
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Box>
        )}
      </Box>

      {/* Description Section */}
      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1e3a8a', mb: 0.5, mt: 1, fontSize: '0.75rem' }}>
        Additional Details
      </Typography>

      <div className="form-field">
        <TextField
          fullWidth
          label="Description"
          placeholder="Enter description (optional, max 150 chars)"
          multiline
          rows={2}
          value={formData.Description || formData.description || ''}
          onChange={(e) => handleInputChange('Description', e.target.value)}
          disabled={modalAction === 'view'}
          inputProps={{ maxLength: 150 }}
          helperText={`${(formData.Description || formData.description || '').length}/150 characters`}
        />
      </div>
    </div>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      className="account-head-modal"
    >
      <DialogTitle sx={{ p: 0 }}>
        <div className="modal-title-container">
          <div className="modal-title-text">
            <h2>{getModalTitle().title}</h2>
          </div>
          <Button onClick={onClose} className="modal-close-btn"><Close /></Button>
        </div>
      </DialogTitle>

      <DialogContent>{renderForm()}</DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="outlined">Cancel</Button>
        {modalAction !== 'view' && (
          <Button onClick={handleSave} variant="contained" disabled={loading}>
            {modalAction === 'create' ? 'Add Other Option' : 'Update'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default OtherOptionModal;
